import type { Alarm, Geofence, LonLat } from "@fts/core";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { LazyBoundary } from "../components/LazyBoundary";
import { Panel } from "../components/ui";
import { useI18n, type MessageKey } from "../i18n";
import { kindRule } from "../lib/geofences";
import { useSession } from "../state/session";

const AlarmGeofenceMap = lazy(() => import("./AlarmGeofenceMap"));

/**
 * Zone and violation point of a geofence alarm (ADR 0015). alarms.geofence_id is ON DELETE SET NULL: a null
 * id means the zone was deleted; an id whose row cannot be read (no access, or loading failed) is shown as
 * "not available", never as deleted.
 */
export function AlarmGeofencePanel({ alarm }: { alarm: Pick<Alarm, "id" | "type" | "geofenceId" | "lat" | "lon"> }) {
  const { t, locale } = useI18n();
  const { provider } = useSession();
  const [zone, setZone] = useState<Geofence | null | undefined>(alarm.geofenceId ? undefined : null);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    if (!alarm.geofenceId) return;
    let cancelled = false;
    provider
      .getGeofence(alarm.geofenceId)
      .then((g) => !cancelled && setZone(g))
      .catch(() => !cancelled && setZone(null));
    return () => {
      cancelled = true;
    };
  }, [provider, alarm.geofenceId]);

  const point = useMemo<LonLat | null>(() => (alarm.lat !== null && alarm.lon !== null ? [alarm.lon, alarm.lat] : null), [alarm.lat, alarm.lon]);
  const fmt = (v: number) => v.toLocaleString(locale, { minimumFractionDigits: 5, maximumFractionDigits: 5 });

  return (
    <Panel title={t("alarmZone.title")}>
      {zone === undefined ? (
        <div className="loading" role="status">
          <span className="spinner" aria-hidden="true" />
          {t("common.loading")}
        </div>
      ) : (
        <>
          <dl className="dl">
            <div className="dl-row">
              <dt>{t("alarmZone.zone")}</dt>
              <dd>
                {zone ? (
                  <a href={`#/sites/${encodeURIComponent(zone.siteId)}`} className="subtle-link">
                    {zone.name}
                  </a>
                ) : alarm.geofenceId ? (
                  <span className="muted">{t("alarmZone.unavailable")}</span>
                ) : (
                  <span className="muted">{t("alarmZone.deleted")}</span>
                )}

              </dd>
            </div>
            {zone && (
              <div className="dl-row">
                <dt>{t("alarmZone.kind")}</dt>
                <dd>
                  <span className="zone-kind-cell">
                    <span className={`zone-swatch zone-${zone.kind}`} aria-hidden="true" />
                    <span>
                      {t(`geofence.kind.${zone.kind}` as MessageKey)}
                      <span className="field-hint">{kindRule(t, zone)}</span>
                    </span>
                  </span>
                </dd>
              </div>
            )}
            <div className="dl-row">
              <dt>{t("alarmZone.point")}</dt>
              <dd>{point ? <span className="mono">{`${fmt(point[1])}, ${fmt(point[0])}`}</span> : <span className="muted">{t("alarmZone.noPoint")}</span>}</dd>
            </div>
          </dl>
          {(zone || point) && (
            <div className="zone-alarm-map-wrap">
              {mapError ? (
                <p className="zone-map-error" role="alert">
                  {t("geofence.mapError")}
                </p>
              ) : (
                <LazyBoundary>
                <Suspense
                  fallback={
                    <div className="loading" role="status">
                      <span className="spinner" aria-hidden="true" />
                      {t("common.loading")}
                    </div>
                  }
                >
                  <AlarmGeofenceMap zone={zone} point={point} label={t("alarmZone.mapLabel")} pointLabel={t("alarmZone.pointPin")} onError={() => setMapError(true)} />
                </Suspense>
                </LazyBoundary>
              )}
            </div>
          )}
          <p className="field-hint">{t("alarmZone.noBlock")}</p>
        </>
      )}
    </Panel>
  );
}
